import {Component, OnInit, Injector, OnDestroy} from '@angular/core';
import { ShellConstant } from '@shell_components/constants/shellConstants';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { commonConstants } from '@shell_components/constants/commonConstants';
import { ReportsService } from '@core_services/report/reports.service';
import { AppService } from '@core_services/app.service';
import { PresetReportsComponent } from '@shell_components/reports/preset-reports/preset-reports.component';
import { entitlementConst } from '@shell_components/constants/entitlementConstants';
import { AddlinkService } from '@ems/addlink';
import { Subscription } from 'rxjs';
import { SharedConstant, SharedService, EmtrPagination, EmtrPaginationValues } from '@ems/shared';

@Component({
  selector: 'app-employee-group-association',
  templateUrl: './employee-group-association.component.html'
})
export class EmployeeGroupAssociationComponent implements OnInit, OnDestroy {
  showEmpIdCharLimit: boolean;
  showTextboxCharLimit: boolean;
  allGroupsInfo = ShellConstant.reports.allGroupsInfo;
  getShellConstant = ShellConstant;
  reportDetails: UntypedFormGroup;
  getCommConst = commonConstants;
  fromDate: any;
  toDate: any;
  parent: PresetReportsComponent;
  snapshotSelected: any = true;
  runReportFor: string;
  passReportName: string;
  empGroupReport: string;
  orgID: any;
  private currentOrgSubscription: Subscription;
  disableRunButton: boolean;
  errorMsg: any;
  dateError: any;
  endDateLessThanStartDate = '';
  showClientMsg: any;
  reportName: any;
  sharedConstant = SharedConstant;
  businessUnit: any;
  batchCitiOrg: any;
  dateRangeConstarint = '';
  pageObject: EmtrPagination = EmtrPaginationValues;
  groupDropdownResetToken = 0;
  skipAllGroupValidation = false;
  noGroupsAvailable = false;

  constructor(public addlinkService: AddlinkService, private appService: AppService, public fb: UntypedFormBuilder, public reportService: ReportsService,
              private injector: Injector, public sharedService: SharedService) {
    this.parent = this.injector.get(PresetReportsComponent);
    this.reportName = this.reportService.getReportName();
  }

  ngOnInit() {
    this.reportDetails = this.fb.group({
      dateTo: this.fb.group({
        date: ['', Validators.required],
      }),
      dateFrom: this.fb.group({
        date: ['']
      }),
      accountHolderLastName: '',
      employeeID: '',
      employeeAccount: '',
      employeeLastName: '',
      empGroupName: '',
      tradeSymbol: '',
      cusipSymbol: '',
      selectedButtonType: ''
    });
    this.batchCitiOrg = this.sharedService.applicationInfo.data.batchCitiOrg;
    this.currentOrgSubscription = this.addlinkService.clientInformation.subscribe(clientInfo => {
      if (clientInfo !== null) {
        this.orgID = clientInfo && clientInfo.orgId;
        this.businessUnit = clientInfo && clientInfo.businessUnit;
        this.appService.setCurrentOrg(this.orgID);
        this.reportService.orgId = this.orgID;
        this.pageObject.pageNo = 0;
        this.pageObject.pageSize = 15;
        this.pageObject.sortField = ['name,asc'];
        const isEmployeeGroupReport = this.reportService.selectedReport === this.getShellConstant.reports.presetReports.reportNames.employeeGrpAssociationTxt;
        const hasReportAccess = this.sharedService.checkEntitlements(entitlementConst.navUUID.emtrAccess.ruleSummary.uuid, '', '');
        const isReportValidForBusinessUnit = (this.businessUnit !== this.getShellConstant.rbsBUText && isEmployeeGroupReport)
          || (this.businessUnit === this.getShellConstant.rbsBUText && !isEmployeeGroupReport);

        if (this.orgID !== 0 && isReportValidForBusinessUnit && hasReportAccess) {
          this.noGroupsAvailable = false;
          this.clearNoGroupsAvailableError();
          this.triggerGroupDropdownReset();
          this.setDefaultGroupOrPlanValue();
          /*Freeze the global client selector when the report is selected*/
          this.reportService.freezeGlobalClientSelector(this.reportDetails);
        } else if (this.orgID !== 0) {
          this.cancelReport();
        }
      }
    });
    this.reportDetails.get('selectedButtonType').setValue(this.getShellConstant.reports.RulesDetails.snapShot);
    this.reportDetails.get('selectedButtonType').updateValueAndValidity();
    this.reportService.makeFormDirty(this.reportDetails, this.parent);
    this.onChanges();
    this.reportService.reportsArray.subscribe((data: any) => {
      if (data !== null) {
        data.map((res) => {
          if (res.name === this.reportName) {
            res.accessType === 'CLIENT' ? this.orgID > 0 ? this.showClientMsg = false : this.showClientMsg = true : this.showClientMsg = false;
          }
        });
      }
    });

  }

  editSnapshotHistory(reset?: boolean) {
    const selectedType = this.reportDetails.get('selectedButtonType').value;

    // Suppress transient validation while dropdown remounts and reloads options.
    this.skipAllGroupValidation = Boolean(reset);

    if (reset) {
      // On snapshot/history toggle, clear any stale toast from a previous run.
      // Keep this scoped to toast-only to avoid mutating parent SDPS error state.
      this.parent.msgToast.pushMessage('', '');
      if (!this.noGroupsAvailable) {
        this.reportDetails.get('empGroupName').reset();
        this.triggerGroupDropdownReset();
      }
    }

    if (selectedType === this.getShellConstant.reports.RulesDetails.snapShot) {
      this.reportDetails.get('employeeID').reset();
      this.reportDetails.get('employeeAccount').reset();
      this.reportDetails.get('employeeLastName').reset();
      this.reportDetails.get('accountHolderLastName').reset();
      this.snapshotSelected = true;
      this.skipAllGroupValidation = false;
      this.disableRunButton = false;
      this.clearErrorMsgAllGroups();
      this.dateError = false;
      this.endDateLessThanStartDate = '';
      this.parent.throwDateError('');
      this.parent.clearDateErrors();
      this.reportDetails.get('empGroupName').setValidators([Validators.required]);
    } else {
      this.snapshotSelected = false;
      this.reportDetails.get('empGroupName').setValidators(null);
    }

    this.setDefaultGroupOrPlanValue();
    this.reportDetails.get('empGroupName').updateValueAndValidity();
  }

  groupNameUpdated(e) {
    const allGroupOrPlanName = this.businessUnit === this.getShellConstant.rbsBUText ? this.allGroupsInfo.namePlan : this.allGroupsInfo.name;
    const selectedGroupOrPlan = e?.selectedItem ?? allGroupOrPlanName;
    const normalizedGroupOrPlan = selectedGroupOrPlan === this.allGroupsInfo.groupNameAll ? allGroupOrPlanName : selectedGroupOrPlan;
    this.reportDetails.get('empGroupName').setValue(normalizedGroupOrPlan);

    // During mode-switch remount, ignore transient "All" emissions until final selection settles.
    if (this.skipAllGroupValidation && this.isAllGroupOrPlanSelection(normalizedGroupOrPlan)) {
      this.reportDetails.markAsDirty();
      this.reportDetails.updateValueAndValidity();
      return;
    }

    this.skipAllGroupValidation = false;

    if (!this.snapshotSelected && this.isAllGroupOrPlanSelection(normalizedGroupOrPlan)) {
      this.validateFields(normalizedGroupOrPlan);
    } else {
      this.clearErrorMsgAllGroups();
    }

    this.reportDetails.markAsDirty();
    this.reportDetails.updateValueAndValidity();
  }

  private setDefaultGroupOrPlanValue(): void {
    const allGroupOrPlanName = this.businessUnit === this.getShellConstant.rbsBUText ? this.allGroupsInfo.namePlan : this.allGroupsInfo.name;
    this.reportDetails.get('empGroupName').setValue(allGroupOrPlanName);
  }

  private isAllGroupOrPlanSelection(value: string): boolean {
    return value === this.allGroupsInfo.name || value === this.allGroupsInfo.namePlan || value === this.allGroupsInfo.groupNameAll;
  }

  // Tracks group availability from the shared dropdown and updates error/run state.
  onGroupsAvailable(hasGroups: boolean): void {
    const scheduleErrors = this.parent.schErrorMsg as any[];
    const noGroupsAvailableMessage = this.getShellConstant.reports.presetReports.noGroupsAvailableForReportMsg;
    this.noGroupsAvailable = !hasGroups;
    // Keep parent banner logic decoupled from message text by setting explicit no-groups state.
    this.parent.setNoGroupsOnlyError(this.noGroupsAvailable);

    if (this.noGroupsAvailable) {
      this.disableRunButton = true;
      if (scheduleErrors.indexOf(noGroupsAvailableMessage) === -1) {
        scheduleErrors.push(noGroupsAvailableMessage);
      }
      this.clearErrorMsgAllGroups();
    } else {
      this.clearNoGroupsAvailableError();
      this.disableRunButton = false;
    }
  }

  // Removes only the no-groups validation message while preserving other errors.
  private clearNoGroupsAvailableError(): void {
    const scheduleErrors = this.parent.schErrorMsg as any[];
    const noGroupsMessageIndex = scheduleErrors.indexOf(this.getShellConstant.reports.presetReports.noGroupsAvailableForReportMsg);
    if (noGroupsMessageIndex !== -1) {
      scheduleErrors.splice(noGroupsMessageIndex, 1);
    }
  }

  // Incrementing token requests the shared dropdown to reset internal state.
  private triggerGroupDropdownReset(): void {
    // Token change asks shared dropdown to reset internal state and reload initial options.
    this.groupDropdownResetToken += 1;
  }

  setDate(event) {
    if (!this.snapshotSelected) {
      this.fromDate = event.fromDate;
      this.toDate = event.toDate;
      this.dateError = event.dateError;
    }
  }
  throwDateRangeError(event) {
    this.dateRangeConstarint = event;
    this.parent.throwDateRangeError(event);
  }

  calendarValidation(event) {
    this.parent.calendarValidation(event);
  }

  throwDateError(event) {
    if (!this.snapshotSelected) {
      this.endDateLessThanStartDate = event;
      this.parent.throwDateError(event);
    }
  }

  runReports() {
    if (this.noGroupsAvailable) {
      this.disableRunButton = true;
      return;
    }

    const localInput = {
      employeeId: this.reportDetails.get('employeeID').value ? this.reportDetails.get('employeeID').value.trim() : '',
      accountNumber: this.reportDetails.get('employeeAccount').value ? this.sharedService.removeHypenAccountNum(this.reportDetails.get('employeeAccount').value.trim()) : '',
      employeeLastName: this.reportDetails.get('employeeLastName').value ? this.reportDetails.get('employeeLastName').value.trim().toUpperCase() : '',
      groupName: (this.reportDetails.get('empGroupName').value) ? this.reportDetails.get('empGroupName').value : '',
      endDate: this.snapshotSelected ? '' : encodeURIComponent(this.reportService.formatDate(this.toDate)),
      startDate: this.snapshotSelected ? '' : encodeURIComponent(this.reportService.formatDate(this.fromDate)),
      accountHolderLastName: this.reportDetails.get('accountHolderLastName').value ? this.reportDetails.get('accountHolderLastName').value.trim().toUpperCase() : '',
      orgId: this.reportService.orgId,
      clientName: this.reportService.clientName,
      clientId: this.reportService.clientId
    };
    if (this.snapshotSelected) {
      this.runReportFor = this.getShellConstant.reports.sendReportAPI.snapshotAPI;
      this.empGroupReport = this.getShellConstant.reports.sendReportAPI.empGroupTxt;
      this.passReportName = this.getShellConstant.reports.presetReports.reportNames.employeeGrpAssociationSnapshotReportName;
      this.reportService.selectedSnapshotReport = true;
    } else {
      this.runReportFor = this.getShellConstant.reports.sendReportAPI.empGroup;
      this.passReportName = this.getShellConstant.reports.presetReports.reportNames.employeeGrpAssociationReportName;
    }
    const empGroup = this.snapshotSelected ? 'empgroup' : '';
    this.reportService.executeReports(this.parent, localInput, this.reportService.selectedReport, this.getShellConstant.reports.presetReports.reportNames.employeeGrpAssociationTxt, this.runReportFor,
      this.passReportName, this.appService, empGroup);
  }

  changeInEmpGrpName() {
    if (!this.snapshotSelected && this.isAllGroupOrPlanSelection(this.reportDetails.get('empGroupName').value)) {
      this.validateFields(this.reportDetails.get('empGroupName').value);
    } else {
      this.clearErrorMsgAllGroups();
    }
  }
  onChanges() {
    this.reportDetails.get('empGroupName').valueChanges.subscribe(empGroupNamevalue => {
      this.disableRunButton = false;

      if (this.skipAllGroupValidation || this.snapshotSelected) {
        this.clearErrorMsgAllGroups();
        return;
      }

      if (this.isAllGroupOrPlanSelection(empGroupNamevalue)) {
        this.validateFields(empGroupNamevalue);
      } else {
        this.clearErrorMsgAllGroups();
      }
    });
    this.reportDetails.get('employeeLastName').valueChanges.subscribe(empGroupLastNamevalue => {
      if (empGroupLastNamevalue && this.reportDetails.get('employeeLastName').invalid) {
        this.reportService.errorInEmpLastName = true;
      } else {
        this.reportService.errorInEmpLastName = false;
        this.changeInEmpGrpName();
      }
    });

    this.reportDetails.get('accountHolderLastName').valueChanges.subscribe(accountHolderLastNamevalue => {
      if (accountHolderLastNamevalue && this.reportDetails.get('accountHolderLastName').invalid) {
        this.reportService.errorInAccntHolderLastName = true;
      } else {
        this.reportService.errorInAccntHolderLastName = false;
        this.changeInEmpGrpName();
      }
    });
    this.reportService.formErrorMessage(this.reportDetails, this.parent, this, 'employeeID', this.getShellConstant.reports.presetReports.employeeIdErrorMsg);
    this.reportService.formCharLimitErrorMessage(this.reportDetails, this.parent, 'employeeAccount', 9, this.getShellConstant.manageClients.clientDetailsPage.errorMsg.empAcctNumShouldContain8Digits);
    this.reportService.formErrorMessage(this.reportDetails, this.parent, this, 'employeeAccount', this.businessUnit ? this.sharedConstant[this.businessUnit].empOrParticipantAccountErrMsg : this.getShellConstant.reports.presetReports.empAccountErrMsg);

  }

  validateFields(empGroupNamevalue) {
    if (this.noGroupsAvailable) {
      this.disableRunButton = true;
      return;
    }

    if (this.snapshotSelected) {
      this.disableRunButton = false;
      this.clearErrorMsgAllGroups();
      return;
    }

    if (this.reportDetails.get('employeeID').value || this.reportDetails.get('employeeAccount').value || this.reportDetails.get('accountHolderLastName').value || this.reportDetails.get('employeeLastName').value) {
      this.disableRunButton = false;
      this.clearErrorMsgAllGroups();
    } else {
      if (this.isAllGroupOrPlanSelection(empGroupNamevalue)) {
        this.disableRunButton = true;
        if (this.parent.schErrorMsg.indexOf(this.sharedConstant[this.businessUnit].employeeErrorMsgForAllGroup) === -1) {
          this.parent.schErrorMsg.push(this.sharedConstant[this.businessUnit].employeeErrorMsgForAllGroup);
        }
      } else {
        this.disableRunButton = false;
      }
    }
  }

  clearErrorMsgAllGroups() {
    if (this.parent.schErrorMsg.indexOf(this.sharedConstant[this.businessUnit].employeeErrorMsgForAllGroup) !== -1) {
      this.parent.schErrorMsg.splice(this.parent.schErrorMsg.indexOf(this.sharedConstant[this.businessUnit].employeeErrorMsgForAllGroup), 1);
    }
  }

  cancelReport() {
    this.reportService.cancelReport(this.reportDetails, this.parent);
  }

  ngOnDestroy(): void {
    this.clearNoGroupsAvailableError();
    if (this.currentOrgSubscription) {
      this.currentOrgSubscription.unsubscribe();
    }
    if (!this.snapshotSelected) {
      this.parent.clearErrorMsg();
      this.reportService.errorInEmpLastName = false;
      this.parent.reportFormIsDirty = false;
    }
  }
}
